import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DEFAULT_SITE } from './siteDefaults';
import { stripNewsHtmlToPlain } from './newsHtml';
import { displayNewsDate, isValidIsoDate } from './isoDateDisplay';
import { apiUrl } from './apiBase';

const HEADER_LOGO_URL =
  'https://res.cloudinary.com/dnw8qhp3h/image/upload/v1775968208/WhatsApp_Image_2026-04-12_at_10.13.22_AM_vzenep.jpg';

function CalendarIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M8 5V3M16 5V3M4 9h16M6 5h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowUpRight({ className }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M4 12L12 4M12 4H6M12 4V10"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function NewsPage() {
  const [site, setSite] = useState(() => JSON.parse(JSON.stringify(DEFAULT_SITE)));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  // Web title update
  useEffect(() => {
    const base = 'Rafid Seed';
    document.title = `News | ${base}`;
    return () => {
      document.title = base;
    };
  }, []);

  useEffect(() => {
    fetch(apiUrl('/api/site'))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('unreachable'))))
      .then(setSite)
      .catch(() => {});
  }, []);

  const articles = useMemo(() => {
    const list = Array.isArray(site?.news?.articles) ? site.news.articles : [];
    return list.filter((a) => typeof a?.id === 'string' && a.id.trim());
  }, [site]);

  return (
    <div className="min-h-screen bg-brand-cream/60 text-neutral-900">
      <header className="sticky top-0 z-50 border-b border-neutral-200/90 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/90">
        <div className="mx-auto flex max-w-shell items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2.5 no-underline">
            <img
              src={HEADER_LOGO_URL}
              alt=""
              className="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-neutral-200 sm:h-11 sm:w-11"
            />
            <span className="text-sm font-bold text-brand-forest sm:text-base">Rafid Seed</span>
          </Link>
          <Link
            to="/"
            className="text-sm font-semibold text-brand-lime no-underline transition-colors hover:text-brand-lime-hover"
          >
            Home
          </Link>
        </div>
      </header>

      <main>
        <div className="mx-auto max-w-shell px-4 pb-16 pt-10 sm:px-6 sm:pb-20 sm:pt-12 md:pb-24 lg:px-8">
          <header className="mb-10 text-center md:mb-14">
            <p className="mb-3 text-sm font-bold text-neutral-900">{site.news.sectionLabel || 'Latest news'}</p>
            <h1 className="text-3xl font-bold leading-tight tracking-tight text-neutral-900 sm:text-4xl md:text-[2.5rem] md:leading-[1.2]">
              {site.news.sectionTitle || 'All news'}
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-neutral-600 md:mt-5 md:text-lg">
              Explore updates, stories, and announcements from Rafid Seed.
            </p>
          </header>

          {articles.length > 0 ? (
            <ul className="m-0 grid list-none grid-cols-1 gap-5 p-0 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 lg:gap-6">
              {articles.map(({ id, title, excerpt, image, alt, date, dateIso }, index) => {
                const newsDateLabel = displayNewsDate({ dateIso, date });
                return (
                  <li
                    key={id}
                    className="flex h-full flex-col overflow-hidden rounded-2xl border border-neutral-200/90 bg-white shadow-sm transition-shadow hover:shadow-md"
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-neutral-100">
                      <img
                        src={image}
                        alt={alt || ''}
                        className="h-full w-full object-cover"
                        loading={index < 6 ? 'eager' : 'lazy'}
                        decoding="async"
                      />
                    </div>
                    <div className="flex flex-1 flex-col px-4 pb-5 pt-4 sm:px-5 sm:pb-6 sm:pt-5">
                      {newsDateLabel ? (
                        <p className="mb-2 flex items-center gap-2 text-xs font-medium text-neutral-500 sm:text-sm">
                          <CalendarIcon className="h-4 w-4 shrink-0 text-brand-lime" />
                          <time dateTime={isValidIsoDate(dateIso) ? String(dateIso).trim() : undefined}>
                            {newsDateLabel}
                          </time>
                        </p>
                      ) : null}
                      <h2 className="mb-2 line-clamp-2 text-base font-bold leading-snug text-neutral-900 sm:mb-3 sm:text-[1.0625rem]">
                        {title}
                      </h2>
                      <p className="mb-4 line-clamp-3 flex-1 text-sm leading-relaxed text-neutral-500 sm:mb-5 sm:text-[0.95rem]">
                        {stripNewsHtmlToPlain(excerpt || '')}
                      </p>
                      <Link
                        to={`/news/${encodeURIComponent(id)}`}
                        className="inline-flex items-center gap-1 self-start text-sm font-semibold text-brand-lime no-underline transition-colors hover:text-brand-lime-hover"
                      >
                        Read article
                        <span className="text-base leading-none" aria-hidden>
                          →
                        </span>
                      </Link>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="rounded-2xl border border-neutral-200 bg-white px-6 py-12 text-center text-neutral-600">
              No news articles are published yet.
            </div>
          )}

          <div className="mt-12 flex flex-col items-center justify-center gap-3 border-t border-neutral-200/80 pt-10 sm:flex-row sm:gap-4">
            <Link
              to="/#blog"
              className="inline-flex min-h-[48px] items-center justify-center rounded-2xl border border-neutral-300 bg-white px-6 py-3 text-sm font-semibold text-neutral-800 no-underline transition-colors hover:bg-neutral-50 sm:min-h-0"
            >
              Back to home news
            </Link>
            <Link
              to="/#contact"
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl border-0 bg-brand-lime px-6 py-3 text-sm font-semibold text-brand-forest no-underline transition-colors hover:bg-brand-lime-hover sm:min-h-0"
            >
              Contact us
              <ArrowUpRight className="h-4 w-4 shrink-0" />
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
